import { useEffect, useState, useRef } from "react";
import { ArrowRight, ArrowDown } from "lucide-react";
import { useNavigate } from "react-router-dom";

const stats = [
  { end: 40, suffix: "+", label: "Years" },
  { end: 10000, suffix: "+", label: "Farmers" },
  { end: 1000, suffix: "", label: "Tons/Day" },
];

const Counter = ({ end, suffix, label }: { end: number; suffix: string; label: string }) => {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        let start = 0;
        const step = Math.max(1, Math.floor(end / 50));
        const interval = setInterval(() => {
          start += step;
          if (start >= end) { setCount(end); clearInterval(interval); } else { setCount(start); }
        }, 25);
        observer.disconnect();
      }
    }, { threshold: 0.5 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end]);
  return (
    <div ref={ref} className="text-center">
      <div className="text-3xl md:text-4xl font-bold font-display text-accent">{count.toLocaleString()}{suffix}</div>
      <div className="text-xs uppercase tracking-[0.2em] text-primary-foreground/50 font-body mt-1">{label}</div>
    </div>
  );
};

const Hero = () => {
  const navigate = useNavigate();
  const scrollToAppointment = () => {
    document.getElementById("appointment")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="home" className="relative min-h-screen flex flex-col">
      {/* Background */}
      <div className="absolute inset-0 bg-primary" />

      {/* Large decorative text behind — editorial style */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none select-none">
        <span className="absolute -right-10 top-1/2 -translate-y-1/2 text-[20vw] font-display font-bold text-primary-foreground/[0.03] leading-none whitespace-nowrap">
          SANDHU
        </span>
        <span className="absolute -left-10 top-[15%] text-[12vw] font-display font-bold text-primary-foreground/[0.03] leading-none whitespace-nowrap">
          FEEDS
        </span>
      </div>

      {/* Main content — asymmetric split */}
      <div className="relative z-10 flex-1 flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left — text, takes 7 cols */}
            <div className="lg:col-span-7">
              <div className="inline-block border border-accent/40 rounded-full px-5 py-1.5 mb-8">
                <span className="text-accent font-body text-xs uppercase tracking-[0.25em] font-semibold">
                  Est. 1984 · ISO Certified · Haryana, India
                </span>
              </div>
              
              <h1 className="text-6xl md:text-8xl lg:text-[6.5rem] font-bold text-primary-foreground font-display leading-[0.95] mb-8 tracking-tight">
                Feed
                <br />
                <span className="italic font-normal text-accent">the future</span>
                <br />
                of poultry
              </h1>
              
              <p className="text-lg md:text-xl text-primary-foreground/60 mb-4 max-w-lg leading-relaxed font-body">
                Founded in 1984 by Mukhtiar Singh Sandhu — today led by the Sandhu Brothers, serving 10,000+ farmers across 20+ states with scientifically formulated, BIS-compliant nutrition for broilers and layers.
              </p>
              <p className="text-sm text-primary-foreground/40 mb-10 max-w-lg font-body">
                4 manufacturing plants · 1,000 tonnes daily capacity · ISO 9001:2015 certified
              </p>

              <div className="flex flex-wrap gap-4">
                <button onClick={scrollToAppointment} className="btn-copper group inline-flex items-center">
                  Book Consultation
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </button>
                <button onClick={() => navigate("/products")} className="btn-outline-light inline-flex items-center">
                  Our Products
                </button>
              </div>
            </div>

            {/* Right — stats stacked vertically with accent line */}
            <div className="lg:col-span-5 flex lg:justify-end">
              <div className="flex lg:flex-col gap-8 lg:gap-10 lg:border-l border-primary-foreground/10 lg:pl-12">
                {stats.map((stat) => (
                  <Counter key={stat.label} end={stat.end} suffix={stat.suffix} label={stat.label} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom scroll indicator */}
      <div className="relative z-10 pb-8 flex justify-center">
        <button 
          onClick={() => document.getElementById("products")?.scrollIntoView({ behavior: "smooth" })}
          className="flex flex-col items-center gap-2 text-primary-foreground/30 hover:text-primary-foreground/60 transition-colors"
        >
          <span className="text-xs uppercase tracking-[0.2em] font-body">Scroll</span>
          <ArrowDown className="h-4 w-4 animate-bounce" />
        </button>
      </div>
    </section>
  );
};

export default Hero;
